// File: frontend/src/utils/numberFormatUtils.ts
//
// Compact number formatting helpers used by dashboards and stat cards.
//
// Once the platform grows, KPI values no longer fit inside the small circle
// badges of the admin dashboard ("1.234.567" simply overflows). These helpers
// shorten large values ("1,2k", "3,4M") and expose the exact value separately
// so the caller can render it as a native tooltip (title attribute).

/** Placeholder rendered when a value is not available yet. */
export const NUMBER_PLACEHOLDER = "—";

/** Default locale used for grouping/decimal separators. */
const DEFAULT_LOCALE = "tr-TR";

/** Values below this are always rendered in full — no shortening needed. */
const COMPACT_THRESHOLD = 1000;

/**
 * Scale units, ordered from largest to smallest.
 * "k" thousand, "M" million, "B" billion, "T" trillion.
 */
const COMPACT_UNITS: { limit: number; suffix: string }[] = [
  { limit: 1_000_000_000_000, suffix: "T" },
  { limit: 1_000_000_000,     suffix: "B" },
  { limit: 1_000_000,         suffix: "M" },
  { limit: 1_000,             suffix: "k" },
];

/** True when the value is a usable finite number. */
const isUsable = (value: number | null | undefined): value is number =>
  value !== null && value !== undefined && Number.isFinite(value);

/**
 * Renders the exact value with locale grouping separators (e.g. "1.234.567").
 * Returns the placeholder when the value is missing.
 */
export function formatFullNumber(
  value: number | null | undefined,
  locale: string = DEFAULT_LOCALE
): string {
  if (!isUsable(value)) return NUMBER_PLACEHOLDER;
  return value.toLocaleString(locale);
}

/**
 * Renders the value shortened with a scale suffix when it reaches 1000.
 *
 *   999        -> "999"
 *   1000       -> "1k"
 *   1234       -> "1,2k"
 *   12_345     -> "12,3k"
 *   123_456    -> "123k"
 *   1_234_567  -> "1,2M"
 *
 * One decimal is kept while the scaled value stays under 100, so the badge
 * text never grows past ~5 characters.
 */
export function formatCompactNumber(
  value: number | null | undefined,
  locale: string = DEFAULT_LOCALE
): string {
  if (!isUsable(value)) return NUMBER_PLACEHOLDER;

  const abs = Math.abs(value);
  if (abs < COMPACT_THRESHOLD) return value.toLocaleString(locale);

  const unit = COMPACT_UNITS.find((u) => abs >= u.limit);
  if (!unit) return value.toLocaleString(locale);

  const scaled = value / unit.limit;
  // Keep one decimal only while there is room for it
  const maximumFractionDigits = Math.abs(scaled) < 100 ? 1 : 0;
  const text = scaled.toLocaleString(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits,
  });
  return `${text}${unit.suffix}`;
}

/** Result of {@link formatCompactWithTitle}. */
export interface CompactNumber {
  /** Shortened text to display. */
  text: string;
  /** Exact value for a tooltip — undefined when the text is already exact. */
  title?: string;
}

/**
 * Convenience wrapper returning both the shortened text and, when the value
 * actually got shortened, the exact number for use as a `title` tooltip.
 * When nothing was shortened `title` is left undefined so no redundant
 * tooltip is attached to the element.
 */
export function formatCompactWithTitle(
  value: number | null | undefined,
  locale: string = DEFAULT_LOCALE
): CompactNumber {
  const text = formatCompactNumber(value, locale);
  const full = formatFullNumber(value, locale);
  return { text, title: text === full ? undefined : full };
}